import React, { useEffect } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { useLocation } from 'react-router-dom';
import {
  withComparisonMode,
  withLayoutComparisonUrlPath,
  withSelectedLayoutsForComparison
} from '../../../state/Layouts';

export default function LayoutCompareButton(): React.ReactElement {
  const { t } = useTranslation();
  const location = useLocation();
  const [comparisonMode, setComparisonMode] = useRecoilState(withComparisonMode);
  const [selectedLayoutsForComparison, setSelectedLayoutsForComparison] = useRecoilState(
    withSelectedLayoutsForComparison
  );
  const [comparisonUrlPath, setComparisonUrlPath] = useRecoilState(withLayoutComparisonUrlPath);

  const canCompare = selectedLayoutsForComparison.length === 2;

  const handleCompareClick = () => {
    if (comparisonMode) {
      // Exit comparison mode
      setComparisonMode(false);
      setSelectedLayoutsForComparison([]);
    } else {
      // Enter comparison mode
      setComparisonMode(true);
    }
  };

  const resetComparisonMode = () => {
    setComparisonMode(false);
    setSelectedLayoutsForComparison([]);
    setComparisonUrlPath(getCurrentUrlPath());
  };

  const getCurrentUrlPath = () => {
    return `/${decodeURIComponent(location.pathname.split('/').pop() || '')}`;
  };

  // Reset comparison mode when URL changes
  useEffect(() => {
    const currentUrlPath = getCurrentUrlPath();
    if (currentUrlPath !== comparisonUrlPath) {
      resetComparisonMode();
    }
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    const currentUrlPath = getCurrentUrlPath();
    setComparisonUrlPath(currentUrlPath);
    return () => {
      resetComparisonMode();
      setComparisonUrlPath(undefined);
    };
  }, []);

  return (
    <Button
      variant={comparisonMode ? 'contained' : 'outlined'}
      color="secondary"
      size="small"
      onClick={handleCompareClick}
      disabled={!comparisonMode && !canCompare}
      data-testid="layouts-compare-button"
    >
      {comparisonMode ? t('layouts.exit_compare') : t('layouts.compare')}
    </Button>
  );
}
